"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  Flex,
  Textarea,
  Box,
} from "@chakra-ui/react";
import { Plus, Trash2 } from "lucide-react";
import type { PostCallAnalysisData } from "./PostCallAnalysis.tsx";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface TextAnalysisFormProps {
  onSave: (data: PostCallAnalysisData) => void;
  onCancel: () => void;
  initialData?: PostCallAnalysisData;
}

export const TextAnalysisForm: React.FC<TextAnalysisFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [examples, setExamples] = useState<string[]>(
    initialData?.examples || []
  );
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setExamples(initialData.examples || []);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      type: "string",
      name,
      description,
      examples: examples.length > 0 ? examples : undefined,
    });
  };

  const handleAddExample = () => {
    setExamples([...examples, ""]);
  };

  const handleExampleChange = (index: number, value: string) => {
    const newExamples = [...examples];
    newExamples[index] = value;
    setExamples(newExamples);
  };

  const handleRemoveExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index));
  };

  return (
    <Box mb={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              id="text-analysis-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="detailed_call_summary"
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Description</FormLabel>
            <Textarea
              sx={
                isDark
                  ? {
                      "&::-webkit-scrollbar": {
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#2D3748", // Dark track color
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#4A5568", // Dark thumb color
                        borderRadius: "2px",
                      },
                      // For Firefox
                      scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                      _hover: {
                        scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                      },
                    }
                  : {
                      "&::-webkit-scrollbar": {
                        height: "4px",
                      },
                      "&::-webkit-scrollbar-button": {
                        display: "none",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#fff",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#fff",
                        borderRadius: "2px",
                      },
                      scrollbarColor: "rgb(220, 220, 220) white",
                      _hover: {
                        scrollbarColor: "rgb(220, 220, 220) white",
                      },
                    }
              }
              id="text-analysis-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed summary of the call before you transfer the call to a human agent so that the human agent can understand the context of the call"
              rows={3}
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Format Example (Optional)</FormLabel>
            <VStack spacing={2} align="stretch">
              {examples.map((example, index) => (
                <Flex key={index} gap={2}>
                  <Input
                    id="text-analysis-example"
                    value={example}
                    onChange={(e) => handleExampleChange(index, e.target.value)}
                    placeholder="Detailed call summary of your use case"
                    size="sm"
                    _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                    variant="outline"
                  />
                  <IconButton
                    aria-label="Remove example"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveExample(index)}
                    color={isDark ? "gray.400" : "gray.600"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
                  />
                </Flex>
              ))}
              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddExample}
                color={isDark ? "gray.400" : "gray.600"}
                _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
                alignSelf="flex-start"
              >
                Add
              </Button>
            </VStack>
          </FormControl>

          <Flex justify="flex-end" gap={2} pt={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              colorScheme={isDark ? "black" : "white"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              colorScheme="gray"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Save
            </Button>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};
